export interface SSEEvent {
  event: string;
  data: string;
}

/**
 * A tiny state machine that accepts SSE text in chunks and yields
 * complete events via a callback. Does not handle `id:` or `retry:`
 * fields — we don't need them.
 *
 * Per the SSE spec:
 * - Lines are separated by \n (we also accept \r\n by normalizing)
 * - Fields are `name: value`
 * - Multiple `data:` fields are joined with \n
 * - An empty line dispatches the current event
 * - Lines starting with `:` are comments (ignored) — commonly used for keepalive
 * - Missing `event:` defaults to "message"
 */
export class SSEParser {
  private buffer = '';
  private currentEvent = '';
  private currentData: string[] = [];

  feed(chunk: string, emit: (ev: SSEEvent) => void): void {
    this.buffer += chunk.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    let idx: number;
    while ((idx = this.buffer.indexOf('\n')) !== -1) {
      const line = this.buffer.slice(0, idx);
      this.buffer = this.buffer.slice(idx + 1);
      this.processLine(line, emit);
    }
  }

  private processLine(line: string, emit: (ev: SSEEvent) => void): void {
    if (line === '') {
      // Dispatch
      if (this.currentData.length === 0 && this.currentEvent === '') return;
      emit({
        event: this.currentEvent || 'message',
        data: this.currentData.join('\n'),
      });
      this.currentEvent = '';
      this.currentData = [];
      return;
    }
    if (line.startsWith(':')) return; // comment / keepalive

    const colon = line.indexOf(':');
    const name = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (name === 'event') this.currentEvent = value;
    else if (name === 'data') this.currentData.push(value);
    // ignore other fields (id, retry)
  }
}
